import { useState, useEffect } from 'react'
import message from 'antd/lib/message'
import Form from 'antd/lib/form'
import * as _ from 'lodash'

import { commonHelper } from 'helpers'
import { useGetPersonPresentDuringFactFind } from 'services/clients/clients.service'

import { commonUtils, eventManager } from 'utils'
import { addEditPersonPresentDuringFactFindMutation } from 'gql/client'
import { useCustomMutation } from 'services/shared/mutation'
import { AddEditPersonPresentDuringFactFindPayload } from 'services/shared/mutation.payload'
import { TCustomCheckboxValue } from 'components/CustomCheckbox/CustomCheckbox.types'

const initialState = {
  isOpen: false,
  data: { _id: '', isEdit: false },
  cb: { confirm: () => null, cancel: () => null },
  loading: false,
}

const usePersonPresentDuringFactFindModal = () => {
  const [form] = Form.useForm()
  const addEditPersonPresentDuringFactFind = useCustomMutation<AddEditPersonPresentDuringFactFindPayload>({
    mutation: addEditPersonPresentDuringFactFindMutation,
    refetchQueries: ['getPersonsPresentDuringFactFind'],
  })
  const urlParams = new URLSearchParams(window.location.search)
  const idNumber = Number(urlParams.get('idNumber'))
  const [showAddressDetails, setShowAddressDetails] = useState(true)

  const [modalState, setModalState] = useState(initialState)
  const personPresentDuringFactFindId = modalState?.data?._id
  const { personPresentDuringFactFind, loading: initialDataLoading } = useGetPersonPresentDuringFactFind({
    idNumber,
    personPresentDuringFactFindId,
    skip: !modalState.isOpen || !modalState.data.isEdit,
  })
  const [loading, setLoading] = useState(initialDataLoading)
  const [formData, setFormData] = useState({
    title: personPresentDuringFactFind.title,
    name: personPresentDuringFactFind.name,
    relationshipWithCustomer: personPresentDuringFactFind.relationshipWithCustomer,
    address: {
      formattedAddress: personPresentDuringFactFind?.address?.formattedAddress,
      address1: personPresentDuringFactFind?.address?.address1,
      address2: personPresentDuringFactFind?.address?.address2,
      town: personPresentDuringFactFind?.address?.town,
      county: personPresentDuringFactFind?.address?.county,
      country: personPresentDuringFactFind?.address?.country,
      postcode: personPresentDuringFactFind?.address?.postcode,
    },
    telephone: personPresentDuringFactFind.telephone,
    email: personPresentDuringFactFind.email,
  })

  useEffect(() => {
    setLoading(initialDataLoading)
    const newData = commonUtils.removeTypeNameFromObject(personPresentDuringFactFind)
    delete newData._id
    setFormData({ ...newData })
    form.setFieldsValue({ ...newData })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDataLoading])

  const clearFormData = () => {
    setFormData((prevState) => commonUtils.resetFormData(prevState))
    form.setFieldsValue(commonUtils.resetFormData(formData))
  }

  const handleCustomCheckboxChange = ({ name, value }: { name: string; value: TCustomCheckboxValue }) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleAntdFormInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prevState) => _.set(_.cloneDeep(prevState), e.target.name, e.target.value))
  }

  const handleCustomSelectChange = (name: string, value: string) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const onPlaceSelect = ({ formattedAddress, address1, address2, town, county, country, postcode }: any) => {
    setFormData((prevState) => ({
      ...prevState,
      address: {
        formattedAddress,
        address1,
        address2,
        town,
        county,
        country,
        postcode,
      },
    }))

    form.setFieldValue('address.formattedAddress', formattedAddress)
    form.setFieldValue('address.address1', address1)
    form.setFieldValue('address.address2', address2)
    form.setFieldValue('address.town', town)
    form.setFieldValue('address.county', county)
    form.setFieldValue('address.country', country)
    form.setFieldValue('address.postcode', postcode)
  }

  useEffect(() => {
    eventManager.addListener('add-person-present-during-fact-find-modal', (data: any, cb: any) => {
      setModalState((state) => ({
        ...state,
        isOpen: data !== undefined,
        data,
        cb,
      }))
    })
  }, [setModalState])

  const handleClose = () => {
    setModalState((state) => ({ ...state, loading: false }))
    eventManager.closePersonPresentDuringFactFindModal()
  }

  const handleCancelClick = async () => {
    try {
      setModalState((state) => ({ ...state, loading: true }))
      if (modalState.cb.cancel) {
        await modalState.cb.cancel()
      }
    } catch (err) {
      // console.error(err)
    }
    handleClose()
    clearFormData()
  }

  const handleSubmit = async () => {
    const { errors } = await addEditPersonPresentDuringFactFind({
      idNumber,
      personToEdit: personPresentDuringFactFindId,
      values: formData,
    })
    if (errors?.length) {
      const errMessage = commonHelper.parseGraphqlErrorMessage(errors)
      message.error(errMessage)
      return
    }
    message.success('Operation finished successfully')
  }

  const handleConfirmClick = async () => {
    try {
      setModalState((state) => ({ ...state, loading: true }))
      await handleSubmit()
      clearFormData()
      if (modalState.cb.confirm) {
        await modalState.cb.confirm()
      }
    } catch (err) {
      // console.error(err)
    }
    setModalState((state) => ({ ...state, loading: false }))
    handleClose()
  }

  const showAddressDetailsChange = (checked: boolean) => {
    setShowAddressDetails(checked)
  }

  return {
    modalState,
    handleCancelClick,
    handleConfirmClick,
    formData,
    handleCustomCheckboxChange,
    handleAntdFormInputChange,
    handleCustomSelectChange,
    onPlaceSelect,
    showAddressDetails,
    showAddressDetailsChange,
    loading,
    form,
  }
}

export default usePersonPresentDuringFactFindModal
